/**
 * An overlap in a record panel (Feature 74 D5, D11): each automation that
 * wakes on the same capability and runs the same one, when each wakes (on
 * the call or the completion, Feature 78 D9), and what that means.
 * On the platform it is possible, where both are on; in an organization it
 * is actual, and the way out is to turn one off.
 */
import { Callout, Stack } from '../../ui/finixt.js';
import { describe, wakeWords, type Automation } from './overlaps.js';

export function OverlapNotice({
  self,
  others,
  templates,
}: {
  self: Automation;
  others: readonly Automation[];
  /** True on the platform, where the overlap is only possible. */
  templates: boolean;
}) {
  if (others.length === 0) return null;
  return (
    <Callout tone="warn" label={templates ? 'Overlaps where both are on' : 'Overlaps here'}>
      <Stack gap={7}>
        {others.map((other) => (
          <span key={other.id}>
            {capitalize(describe(other, templates))} also runs{' '}
            <span className="mono">{sharedTargets(self, other).join(', ')}</span> when{' '}
            <span className="mono">{self.source}</span> {wakeWords(other)}
            {Boolean(other.called) !== Boolean(self.called) && (
              <>; this one when it {wakeWords(self)}</>
            )}
            .
          </span>
        ))}
        <span>
          {templates
            ? 'An organization with both on gets each run twice.'
            : 'Each run happens twice here. Turn one off.'}
        </span>
      </Stack>
    </Callout>
  );
}

/** The capabilities both run. */
function sharedTargets(self: Automation, other: Automation): string[] {
  return self.targets.filter((t) => other.targets.includes(t));
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
